export const REWARD_TIER_KEYS = {
  GOLD: 'gold',
  SILVER: 'silver',
  BRONZE: 'bronze',
  TRAVELLER: 'traveller',
  MEMBER: 'member',
};

export const REWARD_BENEFIT_KEYS = {
  PRIORITY_BOARDING: 'priorityBoarding',
};

/** Hidden for now. Set true to show Priority Boarding under reward benefits. */
export const SHOW_PRIORITY_BOARDING_FILTER = false;

/**
 * Ordered tier definitions used for filters and display.
 * `color` is the tier accent used for chips and seat-map row borders.
 */
export const REWARD_TIERS = [
  {
    key: REWARD_TIER_KEYS.GOLD,
    displayName: 'Gold',
    iconKey: 'gold',
    color: '#C9A227',
  },
  {
    key: REWARD_TIER_KEYS.SILVER,
    displayName: 'Silver',
    iconKey: 'silver',
    prefix: 'S',
    color: '#8A8A8A',
  },
  {
    key: REWARD_TIER_KEYS.BRONZE,
    displayName: 'Bronze',
    iconKey: 'bronze',
    prefix: 'B',
    color: '#B87333',
  },
  {
    key: REWARD_TIER_KEYS.TRAVELLER,
    displayName: 'Traveller',
    iconKey: null,
    prefix: 'T',
    color: '#2A5980',
  },
  {
    key: REWARD_TIER_KEYS.MEMBER,
    displayName: 'Member',
    iconKey: null,
    prefix: 'M',
    color: '#5C6B7A',
  },
];

const BENEFIT_SLOT_CHARS = {
  O: null,
  S: {
    key: 'seat',
    label: 'Seat',
    onboard: true,
    displayable: true,
  },
  C: {
    key: 'cabinBag',
    label: 'Cabin Bag',
    onboard: true,
    displayable: true,
  },
  B: {
    key: 'checkedBag',
    label: 'Checked Bag',
    onboard: false,
    displayable: false,
  },
  F: {
    key: 'fastTrack',
    label: 'Fast Track',
    onboard: false,
    displayable: false,
  },
};

const GOLD_HARDCODED_BENEFITS = [
  {
    key: 'prioritySeat',
    label: 'Priority seat',
    onboard: true,
    displayable: true,
    source: 'included',
  },
  {
    key: 'cabinBag',
    label: 'Cabin Bag',
    onboard: true,
    displayable: true,
    source: 'included',
  },
  {
    key: 'hotDrink',
    label: 'Hot Drink',
    onboard: true,
    displayable: true,
    source: 'included',
  },
];

const PRIORITY_BOARDING_BENEFIT = {
  key: REWARD_BENEFIT_KEYS.PRIORITY_BOARDING,
  label: 'Priority Boarding',
  onboard: false,
  displayable: true,
  source: 'service',
};

const TIER_BY_KEY = REWARD_TIERS.reduce((acc, tier) => {
  acc[tier.key] = tier;
  return acc;
}, {});

/** XOO? / BOO? — e.g. XOOO, XOOB, BOOC, BOOS */
const BRONZE_CODE_PATTERN = /^[XB]OO[OBCSF]$/;

/**
 * WO?? / SO?? — e.g. WOOO, WOSB, WOCS, SOFB, SOFC
 * Three sub-patterns: OO?, O?B, and O(CS|FS|FC).
 */
const SILVER_CODE_PATTERNS = [
  /^[WS]OO[OBCSF]$/,
  /^[WS]O[SCF]B$/,
  /^[WS]O(CS|FS|FC)$/,
];

const isSilverCode = (code) =>
  SILVER_CODE_PATTERNS.some((pattern) => pattern.test(code));

/**
 * Resolves an Amadeus 4-char reward code to a tier key, or null if unknown.
 *
 * 1. ZOOO / MOOO → Member
 * 2. YOOO / TOOO → Traveller
 * 3. XOO? / BOO? → Bronze
 * 4. WO?? / SO?? → Silver
 */
const resolveTierKeyFromCode = (code) => {
  if (code.length !== 4) {
    return null;
  }

  const prefix = code.charAt(0);

  if (prefix === 'Z' || prefix === 'M') {
    return REWARD_TIER_KEYS.MEMBER;
  }

  if (prefix === 'Y' || prefix === 'T') {
    return REWARD_TIER_KEYS.TRAVELLER;
  }

  if (BRONZE_CODE_PATTERN.test(code)) {
    return REWARD_TIER_KEYS.BRONZE;
  }

  if (isSilverCode(code)) {
    return REWARD_TIER_KEYS.SILVER;
  }

  return null;
};

const cloneBenefit = (benefit) => ({
  ...benefit,
  source: benefit.source || 'included',
});

const buildGoldReward = (rawCode) => {
  const benefits = GOLD_HARDCODED_BENEFITS.map(cloneBenefit);
  return {
    tierKey: REWARD_TIER_KEYS.GOLD,
    tierDisplayName: 'Gold',
    rawCode,
    iconKey: 'gold',
    includedBenefits: benefits,
    allBenefits: benefits,
  };
};

const PRIORITY_SERVICE_CODES = new Set(['PRIO', 'PRIORITY']);

export const isPriorityServiceCode = (code) =>
  PRIORITY_SERVICE_CODES.has(String(code || '').toUpperCase());

export const passengerHasPriorityService = (passenger) =>
  (passenger?.services || []).some((service) => {
    const code = typeof service === 'string' ? service : service?.code;
    return isPriorityServiceCode(code);
  });

const parseBenefitSlots = (slots) => {
  const allBenefits = [];
  const includedBenefits = [];

  slots.split('').forEach((char) => {
    const definition = BENEFIT_SLOT_CHARS[char?.toUpperCase()];
    if (!definition) return;

    const benefit = cloneBenefit(definition);
    allBenefits.push(benefit);
    if (benefit.displayable) {
      includedBenefits.push(benefit);
    }
  });

  return { allBenefits, includedBenefits };
};

/**
 * Parses an Amadeus reward tier / service code into tier + benefits.
 * Returns null for unknown, empty, or numeric codes.
 */
export const parseRewardCode = (rawCode) => {
  if (!rawCode || typeof rawCode !== 'string') {
    return null;
  }

  const code = rawCode.trim().toUpperCase();
  if (!code || /^\d+$/.test(code)) {
    return null;
  }

  if (code === 'GOLD' || code === 'PRIO') {
    return buildGoldReward(code);
  }

  if (code.length !== 4) {
    return null;
  }

  const tierKey = resolveTierKeyFromCode(code);
  const tier = tierKey ? TIER_BY_KEY[tierKey] : null;
  if (!tier) {
    return null;
  }

  const { allBenefits, includedBenefits } = parseBenefitSlots(code.slice(1, 4));

  return {
    tierKey: tier.key,
    tierDisplayName: tier.displayName,
    rawCode: code,
    iconKey: tier.iconKey,
    includedBenefits,
    allBenefits,
  };
};

export const getPassengerReward = (passenger) =>
  parseRewardCode(passenger?.frequentFlyerTierLevel);

/**
 * Displayable loyalty rewards for a passenger. Priority Boarding is a
 * service benefit, not a reward tier, and is not included here.
 */
export const getPassengerRewards = (passenger) => {
  const primary = getPassengerReward(passenger);
  return primary ? [primary] : [];
};

/**
 * Formats reward display as "Tier (benefits)" with optional future purchased [] suffix.
 */
export const formatRewardDisplay = (reward, { purchased = [] } = {}) => {
  if (!reward?.tierDisplayName) {
    return '';
  }

  const benefitLabels = (reward.includedBenefits || [])
    .map((benefit) => benefit.label)
    .filter(Boolean);

  let result = reward.tierDisplayName;
  if (benefitLabels.length > 0) {
    result += ` (${benefitLabels.join(', ')})`;
  }

  const purchasedLabels = (purchased || [])
    .map((benefit) => benefit?.label || benefit)
    .filter(Boolean);

  if (purchasedLabels.length > 0) {
    result += ` [${purchasedLabels.join(', ')}]`;
  }

  return result;
};

export const formatPassengerRewardDisplay = (passenger) =>
  getPassengerRewards(passenger)
    .map((reward) => formatRewardDisplay(reward))
    .filter(Boolean)
    .join(', ');

export const passengerHasBenefit = (reward, benefitKey) => {
  if (!reward || !benefitKey) {
    return false;
  }

  return (reward.includedBenefits || []).some(
    (benefit) => benefit.key === benefitKey
  );
};

export const passengerHasRewardBenefit = (passenger, benefitKey) => {
  if (!benefitKey) {
    return false;
  }

  if (benefitKey === REWARD_BENEFIT_KEYS.PRIORITY_BOARDING) {
    return passengerHasPriorityService(passenger);
  }

  return passengerHasBenefit(getPassengerReward(passenger), benefitKey);
};

export const getVisibleRewardBenefitFilters = (filters = []) => {
  if (SHOW_PRIORITY_BOARDING_FILTER) {
    return filters;
  }

  return filters.filter(
    (item) => item.key !== REWARD_BENEFIT_KEYS.PRIORITY_BOARDING
  );
};

/**
 * Builds dynamic benefit filter options from passengers currently in scope.
 * Seat and Priority seat stay separate: Gold does not count towards Seat.
 * Priority Boarding is counted from the PRIO/PRIORITY service code.
 */
export const buildDynamicBenefitFilters = (passengers = []) => {
  const benefitMap = new Map();

  const addBenefit = (benefit) => {
    if (!benefit?.displayable) return;

    const existing = benefitMap.get(benefit.key) || {
      key: benefit.key,
      label: benefit.label,
      count: 0,
    };
    existing.count += 1;
    benefitMap.set(benefit.key, existing);
  };

  passengers.forEach((passenger) => {
    const reward = getPassengerReward(passenger);
    (reward?.includedBenefits || []).forEach(addBenefit);

    if (passengerHasPriorityService(passenger)) {
      addBenefit(PRIORITY_BOARDING_BENEFIT);
    }
  });

  return Array.from(benefitMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

/**
 * Builds per-tier passenger counts for the reward filter menu.
 */
export const buildRewardTierCounts = (passengers = []) => {
  const counts = REWARD_TIERS.reduce((acc, tier) => {
    acc[tier.key] = 0;
    return acc;
  }, {});

  passengers.forEach((passenger) => {
    const reward = getPassengerReward(passenger);
    if (reward?.tierKey && counts[reward.tierKey] !== undefined) {
      counts[reward.tierKey] += 1;
    }
  });

  return counts;
};

export const isRewardServiceCode = (code) => {
  if (!code || typeof code !== 'string') return false;
  const normalized = code.trim().toUpperCase();
  return (
    normalized === 'GOLD' ||
    normalized === 'PRIO' ||
    normalized === 'PRIORITY' ||
    !!parseRewardCode(normalized)
  );
};

/**
 * Logs a summary of frequentFlyerTierLevel values for debugging reward tier parsing.
 */
export const logRewardTierDiagnostics = (passengers = [], context = '') => {
  if (!Array.isArray(passengers) || passengers.length === 0) {
    console.info(
      `[RewardTier]${context ? ` ${context}` : ''} No passengers to inspect`
    );
    return;
  }

  const tierCounts = {};
  const samples = {};

  passengers.forEach((passenger) => {
    const tierLevel = passenger?.frequentFlyerTierLevel;
    const key =
      tierLevel === null || tierLevel === undefined || tierLevel === ''
        ? '(null)'
        : String(tierLevel);

    tierCounts[key] = (tierCounts[key] || 0) + 1;

    if (!samples[key]) {
      const reward = getPassengerReward(passenger);
      samples[key] = {
        frequentFlyerTierLevel: passenger?.frequentFlyerTierLevel ?? null,
        frequentFlyerNumber: passenger?.frequentFlyerNumber ? '(present)' : null,
        parsedReward: formatRewardDisplay(reward) || '(none)',
      };
    }
  });

  console.info(
    `[RewardTier]${context ? ` ${context}` : ''} frequentFlyerTierLevel summary`,
    {
      passengerCount: passengers.length,
      uniqueTiers: Object.keys(tierCounts).length,
      tierCounts,
      samples,
    }
  );
};
